import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";


const EMPTY_ACCOUNT = {
  fivem_connected: false,
  fivem_username: null,
  discord_connected: false,
  discord_username: null,
};

const CommerceContext = createContext(null);

const readStoredCart = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("aerocore_cart") || "[]");
    return Array.isArray(stored)
      ? [...new Set(stored.filter((item) => typeof item === "string"))]
      : [];
  } catch {
    return [];
  }
};

export const CommerceProvider = ({ children }) => {
  const [account, setAccount] = useState(EMPTY_ACCOUNT);
  const [accountLoading, setAccountLoading] = useState(true);
  const [cartIds, setCartIds] = useState(readStoredCart);

  const refreshAccount = useCallback(async () => {
    setAccountLoading(true);
    try {
      const response = await api.get("/checkout/status");
      setAccount(response.data);
      return response.data;
    } catch {
      setAccount(EMPTY_ACCOUNT);
      return EMPTY_ACCOUNT;
    } finally {
      setAccountLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAccount();
  }, [refreshAccount]);

  const startFiveM = useCallback(async (returnPath = window.location.pathname) => {
    const response = await api.post("/checkout/fivem/start", { return_path: returnPath });
    window.location.assign(response.data.auth_url);
  }, []);

  const startDiscord = useCallback(async (returnPath = window.location.pathname) => {
    const response = await api.post("/checkout/discord/start", { return_path: returnPath });
    window.location.assign(response.data.auth_url);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/checkout/logout");
    setAccount(EMPTY_ACCOUNT);
  }, []);

  const storeCart = useCallback((nextCart) => {
    localStorage.setItem("aerocore_cart", JSON.stringify(nextCart));
    setCartIds(nextCart);
  }, []);

  const addToCart = useCallback((productId) => {
    if (cartIds.includes(productId)) return false;
    storeCart([...cartIds, productId]);
    return true;
  }, [cartIds, storeCart]);

  const removeFromCart = useCallback((productId) => {
    storeCart(cartIds.filter((item) => item !== productId));
  }, [cartIds, storeCart]);

  const clearCart = useCallback(() => {
    storeCart([]);
  }, [storeCart]);

  const value = useMemo(() => ({
    account,
    accountLoading,
    refreshAccount,
    startFiveM,
    startDiscord,
    logout,
    cartIds,
    cartCount: cartIds.length,
    addToCart,
    removeFromCart,
    clearCart,
    isInCart: (productId) => cartIds.includes(productId),
  }), [
    account,
    accountLoading,
    refreshAccount,
    startFiveM,
    startDiscord,
    logout,
    cartIds,
    addToCart,
    removeFromCart,
    clearCart,
  ]);

  return <CommerceContext.Provider value={value}>{children}</CommerceContext.Provider>;
};

export const useCommerce = () => {
  const context = useContext(CommerceContext);
  if (!context) throw new Error("useCommerce must be used inside CommerceProvider");
  return context;
};
